/* eslint-disable indent */
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import 'plyr/dist/plyr.css';
import { BoundingBox } from '@/type/detection.types';
import { useDetectionData } from '@/hooks/load-detection.hooks';
import { twMerge } from 'tailwind-merge';
import { Spinner } from '@/components/Loading/Spinner';
// import Loader from '@/components/Loading/Loader';
import { Switch } from '@/components/Controls/Switch';
import { MediaNotFound } from '@/components/Message/MediaNotFound';
import { TFunction } from 'i18next';
import './styles.css';
import {
  useVideoPlayer,
  useVideoPlayerChapters,
} from '@/hooks/video-player.hooks';

interface DetectionSwitchesProps {
  containerClassName?: string;
}

interface DetectionVideoPlayerProps {
  t: TFunction<undefined>;
  videoRef: RefObject<HTMLVideoElement>;
  videoUrl?: string;
  isLoadingVideo?: boolean;
  detectionFiles?: {
    damagesUrl?: string;
    detectionsUrl?: string;
  };
  showDetectionSwitchesAboveVideo?: boolean;
  showEquipmentDetectionSwitch?: boolean;
  showDamageDetectionSwitch?: boolean;
  chapterData?: Array<{
    startTime: number;
    endTime: number;
    title: string;
  }>;
  videoContainerClassName?: string;
  toggleTextClassName?: string;
  videoClassName?: string;
  detectionSwitchesContainerClassName?: string;
  detectionSwitchesToggleTextClassName?: string;
  detectionSwitchesContentClassName?: string;
  mediaNotFoundContainerClassName?: string;
  onLoadedMetadata?: () => void;
}

const DEFAULT_ORIGINAL_WIDTH = 250;
const DEFAULT_ORIGINAL_HEIGHT = 420;

const DetectionVideoPlayer = ({
  t,
  videoUrl,
  isLoadingVideo,
  detectionFiles,
  showDetectionSwitchesAboveVideo = true,
  showEquipmentDetectionSwitch = true,
  showDamageDetectionSwitch = true,
  chapterData,
  videoContainerClassName,
  toggleTextClassName,
  videoClassName,
  detectionSwitchesContainerClassName,
  detectionSwitchesToggleTextClassName,
  detectionSwitchesContentClassName,
  mediaNotFoundContainerClassName,
  onLoadedMetadata,
  videoRef,
}: DetectionVideoPlayerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentTimestamp, setCurrentTimestamp] = useState<number>(0);
  const [originalDimensions, setOriginalDimensions] = useState({
    width: DEFAULT_ORIGINAL_WIDTH,
    height: DEFAULT_ORIGINAL_HEIGHT,
  });

  const [isVideoBuffering, setIsVideoBuffering] = useState(true);
  const [showDetection, setShowDetection] = useState(true);
  const [showDamage, setShowDamage] = useState(true);
  const [videoRenderDimensions, setVideoRenderDimensions] = useState({
    width: 0,
    height: 0,
  });

  const { playerHolderRef, plyrContainer } = useVideoPlayer({
    videoUrl,
    videoRef,
    videoClassName,
    chapters: chapterData,
    // Send the outer wrapper (not just .plyr) into fullscreen so the detection
    // canvas overlay stays visible above the video.
    fullscreenContainer: '.detection-player-root',
  });

  useVideoPlayerChapters({
    chapters: chapterData,
    plyrContainer,
    videoRef,
    videoUrl,
  });

  // Use React Query hooks for data fetching - only load when needed
  const { data: damages = [], isLoading: isLoadingDamages } = useDetectionData(
    showDamage ? detectionFiles?.damagesUrl : undefined,
  );

  const { data: detections = [], isLoading: isLoadingDetections } =
    useDetectionData(showDetection ? detectionFiles?.detectionsUrl : undefined);

  // Update original dimensions when detection data is loaded
  useEffect(() => {
    let nextWidth = DEFAULT_ORIGINAL_WIDTH;
    let nextHeight = DEFAULT_ORIGINAL_HEIGHT;

    if (detections && detections.length > 0) {
      const [width, height] = detections[0].video_size.split('x').map(Number);
      nextWidth = width;
      nextHeight = height;
    }

    setOriginalDimensions((prev) => {
      if (prev.width === nextWidth && prev.height === nextHeight) {
        return prev;
      }
      return { width: nextWidth, height: nextHeight };
    });
  }, [detections]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const updateTimestamp = () => {
      setCurrentTimestamp(video.currentTime);
    };

    video.addEventListener('timeupdate', updateTimestamp);
    video.addEventListener('pause', updateTimestamp);
    video.addEventListener('seeked', updateTimestamp);

    return () => {
      video.removeEventListener('timeupdate', updateTimestamp);
      video.removeEventListener('pause', updateTimestamp);
      video.removeEventListener('seeked', updateTimestamp);
    };
  }, [videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const updateActualVideoDimensions = () => {
      const elementRect = video.getBoundingClientRect();
      // The <video> element gets stretched by Plyr / fullscreen, but the
      // actual frame inside it is letterboxed/pillarboxed to preserve the
      // video's aspect ratio. Compute that *content* rect so the canvas
      // overlay matches the visible frame, not the stretched element.
      const intrinsicW = video.videoWidth;
      const intrinsicH = video.videoHeight;
      let contentWidth = elementRect.width;
      let contentHeight = elementRect.height;
      if (
        intrinsicW > 0 &&
        intrinsicH > 0 &&
        contentWidth > 0 &&
        contentHeight > 0
      ) {
        const videoAspect = intrinsicW / intrinsicH;
        const elementAspect = contentWidth / contentHeight;
        if (videoAspect > elementAspect) {
          // Video wider than element → letterbox top/bottom
          contentHeight = contentWidth / videoAspect;
        } else {
          // Video taller than element → pillarbox left/right
          contentWidth = contentHeight * videoAspect;
        }
      }

      // Only update state if the dimensions actually changed
      setVideoRenderDimensions((prev) => {
        if (prev.width === contentWidth && prev.height === contentHeight) {
          return prev;
        }
        // Also update the canvas size to match exactly
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = contentWidth;
          canvas.height = contentHeight;
          canvas.style.width = `${contentWidth}px`;
          canvas.style.height = `${contentHeight}px`;
        }
        return { width: contentWidth, height: contentHeight };
      });
    };

    // Entering/exiting fullscreen reflows the video without firing window
    // resize; rAF lets the browser finish the layout before we measure.
    const onFullscreenChange = () => {
      requestAnimationFrame(updateActualVideoDimensions);
    };

    // Update on important video events and window resize
    video.addEventListener('loadedmetadata', updateActualVideoDimensions);
    video.addEventListener('loadeddata', updateActualVideoDimensions);
    video.addEventListener('play', updateActualVideoDimensions);
    video.addEventListener('pause', updateActualVideoDimensions);
    window.addEventListener('resize', updateActualVideoDimensions);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);

    // Call it immediately if the video is already loaded
    if (video.readyState >= 1) {
      updateActualVideoDimensions();
    }

    return () => {
      video.removeEventListener('loadedmetadata', updateActualVideoDimensions);
      video.removeEventListener('loadeddata', updateActualVideoDimensions);
      video.removeEventListener('play', updateActualVideoDimensions);
      video.removeEventListener('pause', updateActualVideoDimensions);
      window.removeEventListener('resize', updateActualVideoDimensions);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        onFullscreenChange,
      );
    };
  }, [videoRef]);

  const forceRedrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const drawBoxes = (
      boxes: BoundingBox[],
      color: string,
      isDamage: boolean,
    ) => {
      const threshold = 0.01;
      const matchingBoxes = boxes.filter(
        (box) => Math.abs(box.timestamp - currentTimestamp) <= threshold,
      );

      const scaleX = videoRenderDimensions.width / originalDimensions.width;
      const scaleY = videoRenderDimensions.height / originalDimensions.height;

      matchingBoxes.forEach((box) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.fillStyle = color;

        if (isDamage && box.polygon_points && box.polygon_points.length > 0) {
          ctx.beginPath();
          const firstPoint = box.polygon_points[0];
          ctx.moveTo(firstPoint.x * scaleX, firstPoint.y * scaleY);

          for (let i = 1; i < box.polygon_points.length; i++) {
            const point = box.polygon_points[i];
            ctx.lineTo(point.x * scaleX, point.y * scaleY);
          }

          ctx.closePath();
          ctx.stroke();
          ctx.fillStyle = `${color}33`; // Add transparency to the fill
          ctx.fill();

          ctx.font = '12px Arial';
          ctx.fillStyle = color; // Reset fill style for text
          ctx.fillText(
            `${box.className} (${Math.round(box.confidence * 100)}%)`,
            box.polygon_points[0].x * scaleX,
            box.polygon_points[0].y * scaleY - 5,
          );
        } else {
          const scaledX1 = box.x1 * scaleX;
          const scaledY1 = box.y1 * scaleY;
          const scaledX2 = box.x2 * scaleX;
          const scaledY2 = box.y2 * scaleY;

          ctx.beginPath();
          ctx.rect(
            scaledX1,
            scaledY1,
            scaledX2 - scaledX1,
            scaledY2 - scaledY1,
          );
          ctx.stroke();

          ctx.font = '12px Arial';
          ctx.fillText(
            `${box.className} (${Math.round(box.confidence * 100)}%)`,
            scaledX1,
            scaledY1 - 5,
          );
        }
      });
    };

    if (damages.length > 0 && showDamage) {
      drawBoxes(damages, 'red', true);
    }

    if (detections.length > 0 && showDetection) {
      drawBoxes(detections, 'blue', false);
    }
  }, [
    currentTimestamp,
    damages,
    detections,
    originalDimensions,
    showDamage,
    showDetection,
    videoRenderDimensions,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const handlePause = () => {
      // Force redraw when paused
      forceRedrawCanvas();
    };

    const handleSeeked = () => {
      // Force redraw when seeked
      forceRedrawCanvas();
    };

    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);

    return () => {
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [forceRedrawCanvas, videoRef]);

  useEffect(() => {
    forceRedrawCanvas();
  }, [
    currentTimestamp,
    damages,
    detections,
    originalDimensions,
    showDamage,
    showDetection,
    videoRenderDimensions,
    forceRedrawCanvas,
  ]);

  // Forward the loadedmetadata event to the parent.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !onLoadedMetadata) return undefined;

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [onLoadedMetadata, videoRef]);

  // Track the video's buffering state to show an overlay loader until it is ready to play.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const markBuffering = () => setIsVideoBuffering(true);
    const markReady = () => setIsVideoBuffering(false);

    video.addEventListener('loadstart', markBuffering);
    video.addEventListener('waiting', markBuffering);
    video.addEventListener('stalled', markBuffering);
    video.addEventListener('canplay', markReady);
    video.addEventListener('playing', markReady);
    video.addEventListener('loadeddata', markReady);

    return () => {
      video.removeEventListener('loadstart', markBuffering);
      video.removeEventListener('waiting', markBuffering);
      video.removeEventListener('stalled', markBuffering);
      video.removeEventListener('canplay', markReady);
      video.removeEventListener('playing', markReady);
      video.removeEventListener('loadeddata', markReady);
    };
  }, [videoRef, videoUrl]);

  const DetectionSwitches = ({
    containerClassName,
  }: DetectionSwitchesProps) => {
    return (
      <div
        className={twMerge(
          'flex justify-center',
          containerClassName,
          detectionSwitchesContainerClassName,
        )}
      >
        <div
          className={twMerge(
            'mb-6 flex flex-col justify-center flex-wrap max-w-[370px] gap-3',
            detectionSwitchesContentClassName,
          )}
        >
          {showEquipmentDetectionSwitch && (
            <Switch
              leftText={t('displayEquipmentDetection')}
              leftTextClassName={twMerge(
                'flex-1 mr-2',
                toggleTextClassName,
                detectionSwitchesToggleTextClassName,
              )}
              checked={showDetection}
              onChange={() => setShowDetection((prevState) => !prevState)}
              disabled={isLoadingDetections}
            />
          )}
          {showDamageDetectionSwitch && (
            <Switch
              leftText={t('displayDamageDetection')}
              switchClassName={twMerge('', showDamage && '!bg-red-FF2929')}
              leftTextClassName={twMerge(
                'flex-1 mr-2',
                toggleTextClassName,
                detectionSwitchesToggleTextClassName,
              )}
              checked={showDamage}
              onChange={() => setShowDamage((prevState) => !prevState)}
              disabled={isLoadingDamages}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {videoUrl || isLoadingVideo || isVideoBuffering ? (
        <>
          {showDetectionSwitchesAboveVideo &&
            detectionFiles &&
            (showEquipmentDetectionSwitch || showDamageDetectionSwitch) && (
              <DetectionSwitches />
            )}
          <div
            className={twMerge(
              // 'detection-player-root relative bg-gradient-to-t from-black to-transparent rounded-lg overflow-hidden',
              'detection-player-root relative bg-gradient-to-t from-black to-transparent overflow-hidden',
              (isLoadingVideo || isVideoBuffering) && 'min-h-[335px] bg-black',
              videoContainerClassName,
            )}
          >
            <div ref={playerHolderRef} className="contents" />
            {(isLoadingVideo || isVideoBuffering) && <Spinner />}
          </div>
          {!showDetectionSwitchesAboveVideo &&
            detectionFiles &&
            (showEquipmentDetectionSwitch || showDamageDetectionSwitch) && (
              <DetectionSwitches containerClassName="pt-4" />
            )}
        </>
      ) : (
        <div
          className={twMerge(
            'relative h-[50vh] w-full',
            mediaNotFoundContainerClassName,
          )}
        >
          <MediaNotFound
            defaultIconClassName="ki-youtube"
            notFoundText={
              !isLoadingVideo ? t('common:noVideoFound') : undefined
            }
          />
        </div>
      )}
      {/* {(isLoadingDamages || isLoadingDetections) && <Loader />} */}
    </>
  );
};

export default DetectionVideoPlayer;
